/**
 * Phone verification card for /app/profile.
 *
 * Two-step flow:
 *  1. User types a phone number → POST /api/customer/profile/phone/send-code
 *     We display a 6-digit OTP input.
 *  2. User types the SMS code → POST /api/customer/profile/phone/verify
 *     The phone is locked onto the user record and Callback Calling unlocks.
 *
 * A verified phone is REQUIRED to place real Twilio calls via Callback Dialing.
 * The dialer surfaces a CTA back here when this is missing.
 */
import { useState } from "react";
import { Loader2, Phone, ShieldCheck, X } from "lucide-react";
import { toast } from "sonner";
import { useCustomerAuth, describeAxiosError } from "@/contexts/CustomerAuthContext";
import { walletApi } from "@/lib/wallet";

export default function PhoneVerifyCard() {
  const { user, refresh } = useCustomerAuth();
  const verified = !!user?.phone_verified_at && !!user?.phone_e164;

  const [phone, setPhone] = useState(user?.phone_e164 || "");
  const [code, setCode] = useState("");
  const [stage, setStage] = useState("idle"); // idle | sent
  const [busy, setBusy] = useState(false);

  async function sendCode() {
    if (!phone.startsWith("+") || phone.length < 7) {
      toast.error("Enter your phone in international format, e.g. +447911123456");
      return;
    }
    setBusy(true);
    try {
      await walletApi.post("/customer/profile/phone/send-code", { phone });
      setStage("sent");
      toast.success("Verification code sent. Check your SMS.");
    } catch (e) {
      toast.error(describeAxiosError(e, "Could not send the code."));
    } finally { setBusy(false); }
  }

  async function verify() {
    if (code.length < 4) { toast.error("Enter the code from the SMS"); return; }
    setBusy(true);
    try {
      const { data } = await walletApi.post("/customer/profile/phone/verify", { phone, code });
      toast.success(`Phone verified: ${data.phone}`);
      setStage("idle"); setCode("");
      await refresh?.();
    } catch (e) {
      toast.error(describeAxiosError(e, "That code didn't work."));
    } finally { setBusy(false); }
  }

  async function unverify() {
    if (!window.confirm("Remove your verified phone? You won't be able to place calls until you verify again.")) return;
    setBusy(true);
    try {
      await walletApi.delete("/customer/profile/phone");
      toast.success("Phone removed.");
      setPhone(""); setStage("idle");
      await refresh?.();
    } catch (e) {
      toast.error(describeAxiosError(e, "Could not remove the phone."));
    } finally { setBusy(false); }
  }

  return (
    <section className="rounded-2xl border border-brand-line bg-white p-6 lg:p-8" data-testid="phone-verify-card">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="font-display font-bold text-lg flex items-center gap-2">
            <Phone className="h-4 w-4 text-brand-green" /> Phone number
          </h2>
          <p className="mt-1 text-sm text-brand-muted">
            Required to place real calls — we ring your phone first, then connect you to the destination.
          </p>
        </div>
        {verified && (
          <span className="shrink-0 inline-flex items-center gap-1 rounded-full bg-brand-greenSoft text-brand-greenDark px-3 py-1 text-[11px] font-bold uppercase tracking-[0.2em]" data-testid="phone-verified-badge">
            <ShieldCheck className="h-3 w-3" /> Verified
          </span>
        )}
      </div>

      {verified ? (
        <div className="mt-5 flex items-center justify-between gap-3 rounded-xl bg-brand-bgSoft px-4 py-3">
          <div className="min-w-0">
            <p className="font-mono text-base font-bold text-brand-ink truncate" data-testid="phone-verified-number">{user.phone_e164}</p>
            <p className="text-[11px] text-brand-muted">Verified {user.phone_verified_at?.slice(0, 10)}</p>
          </div>
          <button onClick={unverify} disabled={busy} data-testid="phone-remove-btn"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-red-600 hover:text-red-700 disabled:opacity-50">
            <X className="h-3.5 w-3.5" /> Remove
          </button>
        </div>
      ) : stage === "idle" ? (
        <div className="mt-5 flex items-end gap-2">
          <div className="flex-1">
            <label className="text-[11px] uppercase tracking-[0.22em] text-brand-muted font-bold">Your phone</label>
            <input
              type="tel" inputMode="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
              placeholder="+447911123456" data-testid="phone-input"
              className="mt-1 w-full rounded-xl border border-brand-line px-3 py-2.5 text-sm font-medium focus:border-brand-green focus:outline-none focus:ring-4 focus:ring-brand-green/15"
            />
          </div>
          <button onClick={sendCode} disabled={busy} data-testid="phone-send-code-btn"
            className="inline-flex items-center gap-1.5 rounded-full bg-brand-green hover:bg-brand-greenHover text-brand-ink font-bold text-sm px-4 py-2.5 disabled:opacity-50">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send code"}
          </button>
        </div>
      ) : (
        <div className="mt-5 space-y-3">
          <p className="text-xs text-brand-muted">Code sent to <span className="font-mono font-bold text-brand-ink">{phone}</span></p>
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <label className="text-[11px] uppercase tracking-[0.22em] text-brand-muted font-bold">6-digit code</label>
              <input
                type="text" inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                placeholder="••••••" data-testid="phone-code-input"
                className="mt-1 w-full rounded-xl border border-brand-line px-3 py-2.5 text-base font-mono tracking-[0.45em] text-center focus:border-brand-green focus:outline-none focus:ring-4 focus:ring-brand-green/15"
              />
            </div>
            <button onClick={verify} disabled={busy} data-testid="phone-verify-btn"
              className="inline-flex items-center gap-1.5 rounded-full bg-brand-green hover:bg-brand-greenHover text-brand-ink font-bold text-sm px-4 py-2.5 disabled:opacity-50">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify"}
            </button>
          </div>
          <button onClick={() => { setStage("idle"); setCode(""); }} className="text-xs font-semibold text-brand-muted hover:text-brand-ink" data-testid="phone-change-number-btn">
            ← Use a different number
          </button>
        </div>
      )}
    </section>
  );
}
